import { AudioProcessOutput, MusicNoteOccurrence, MusicNoteSimpleWeight, MusicScale } from "../../wasm/pkg/wasm";
import { AUDIO_BLOB_MILLIS } from "../models/AudioStream.interface";
import { ChordInScaleEdit, sortNotesLowestLast } from "../models/wasm.model";

export interface ChordHistoryPerScale {
  chordHistory: Array<Array<ChordInScaleEdit>>;
  scale: MusicScale;
  /** If a scale has been ruled out for the current section (e.g. by incompatible notes appearing), hide it */
  hideRuledOutScale?: boolean;
  highestNumOfChords: number;
}

export interface AudioHistoryState {
  /** How many ticks (currently at 0.5s intervals) have been captured */
  entryCounter: number;
  /** For each tick, the loudest notes */
  noteHistory: Array<Array<MusicNoteOccurrence>>;
  /** For each tick, how the current loudest notes fit as chords into possible scales */
  chordsInScales: {
    /** scaleId is a MusicNoteIdentity (TS does not allow string set as index -- edit: it might if you use Record, check) */
    [scaleId: string]: ChordHistoryPerScale;
  };
  /** Same length as chordHistory and noteHistory - those are ordered, but could have time-gaps  */
  timestamps: Array<number>;
  timestampMeta: Array<TimestampMeta>;
  weightHistory: WeightHistory;
  isPlaceholder: boolean;
  /** Kept to (1) show newest clearly on UI (2) optimize seek / re-record, to prevent repeated find operations */
  newestTimestamp: number;
  newestTimestampIndex: number;
  primaryScaleId: string | undefined;
  primaryScale: MusicScale | undefined;
}

interface TimestampMeta {
  /** a more granular version of "primaryScaleId," used to keep track of which chord is most likely at each tick */
  mostLikelyScaleId: string | undefined;
}

interface WeightHistory {
  /** use as divisor for normalization for noteHistory */
  biggestSumWeightYet: number;
  /** use as note weight map.  These are normalized to between 0 and 1 in wasm. */
  totalNoteWeights: {
    [noteId: string]: MusicNoteSimpleWeight;
  };
}

/**
 * Encapsulate the decision "how to update all these time-ordered arrays.""
 */
enum HowToAdd {
  Inject,
  Replace,
  Append,
}
type NextPlacement = {
  howToAdd: HowToAdd;
  index: number;
};

export function buildPlaceholderAudioProcessingState(): AudioHistoryState {
  return {
    ...buildBlankAudioProcessingState(),
    entryCounter: 1,
    noteHistory: [[]], // for the moment, convenient way to show 0 seconds UX
    timestamps: [0],
    isPlaceholder: true,
  };
}

export function buildBlankAudioProcessingState(): AudioHistoryState {
  return {
    entryCounter: 0,
    chordsInScales: {},
    noteHistory: [],
    timestamps: [],
    timestampMeta: [],
    weightHistory: {
      /** For individual note history */
      biggestSumWeightYet: 0,
      totalNoteWeights: {},
    },
    isPlaceholder: false,
    newestTimestamp: 0,
    newestTimestampIndex: 0,
    primaryScaleId: undefined,
    primaryScale: undefined,
  };
}

/**
 * Create a new state object with new references, updated to include the latest tick.
 *
 */
export function buildNextAudioProcessingInfo(oldState: AudioHistoryState, wasmOutput: AudioProcessOutput): AudioHistoryState {
  // console.log("Next aud proc info", oldState);
  const placement = calculateNextPlacement(oldState, wasmOutput);

  const notesLowestLast = sortNotesLowestLast(wasmOutput.last01s_loudest_notes);

  let newState: AudioHistoryState = {
    noteHistory: buildNextHistoryArrayPerPlacement(oldState.noteHistory, notesLowestLast, placement),
    timestamps: buildNextHistoryArrayPerPlacement(oldState.timestamps, wasmOutput.time_stamp, placement),
    newestTimestampIndex: placement.index,
    newestTimestamp: wasmOutput.time_stamp,
    chordsInScales: {}, // filled in below
    timestampMeta: [], // filled in below
    entryCounter: oldState.entryCounter + (placement.howToAdd === HowToAdd.Replace ? 0 : 1),
    weightHistory: updateWeightHistory(oldState.weightHistory, wasmOutput),
    isPlaceholder: false,
    primaryScaleId: undefined, // Filled in below
    primaryScale: undefined, // Filled in below
  };
  // capture and organize new chords
  wasmOutput.last01s_chords.forEach(({ scale, chords }) => {
    const scaleId = buildUniqueScaleFamilyId(scale);
    if (oldState.chordsInScales[scaleId]) {
      // add the last 1 second worth of chord info for each scale
      newState.chordsInScales[scaleId] = {
        scale,
        highestNumOfChords: Math.max(oldState.chordsInScales[scaleId].highestNumOfChords, chords.length),
        chordHistory: buildNextHistoryArrayPerPlacement(oldState.chordsInScales[scaleId].chordHistory, chords, placement),
      };
    } else {
      // if a scale hasn't appeared yet (and it does have chords in it) add it and backfill empty entries
      if (chords?.length > 0) {
        const chordHistory = Array(oldState.entryCounter).fill([]);
        newState.chordsInScales[scaleId] = {
          scale,
          highestNumOfChords: chords.length,
          chordHistory: buildNextHistoryArrayPerPlacement(chordHistory, chords, placement),
        };
      }
    }
    // if (newState.chordsInScales[scaleId]?.chordHistory?.length > 0) {
    //   const index = newState.chordsInScales[scaleId].chordHistory.length - 1;
    //   // console.log("  at index=", index, " vs length = ", newState.chordsInScales[scaleId].chordHistory.length);
    //   newState.chordsInScales[scaleId].chordHistory[index].forEach((c) => {
    //     const bassNoteId = c.bass_note_id;
    //     if (!c.notes.map((n) => n.note_id).includes(bassNoteId)) {
    //       console.log("Got chord with weird bass", c.notes, bassNoteId);
    //     }
    //   });
    // }
  });

  const updateWithBlank = (sid: string) => buildNextHistoryArrayPerPlacement(oldState.chordsInScales[sid].chordHistory, [], placement);
  // If we're tracking any scales for which there are no new chords, add empty value
  Object.keys(oldState.chordsInScales).forEach((priorScaleId) => {
    const isHistoryEmpty = !oldState.chordsInScales[priorScaleId].chordHistory.some((c) => c.length > 0);
    if (isHistoryEmpty) {
      // Do nothing.  Otherwise scales with zero chords will pile up
    } else if (!newState.chordsInScales[priorScaleId]) {
      newState.chordsInScales[priorScaleId] = {
        ...oldState.chordsInScales[priorScaleId],
        chordHistory: updateWithBlank(priorScaleId),
        hideRuledOutScale: true,
      };
    } else if (newState.chordsInScales[priorScaleId].chordHistory.length < newState.entryCounter) {
      newState.chordsInScales[priorScaleId].chordHistory = updateWithBlank(priorScaleId);
    }
  });

  newState.timestampMeta = buildNextHistoryArrayPerPlacement(
    oldState.timestampMeta,
    buildTimestampMetaForOneTick(newState, placement.index),
    placement
  );

  // sanity check
  if (
    Object.keys(newState.chordsInScales)
      .map((sid) => newState.chordsInScales[sid].chordHistory.length)
      .some((len) => len !== newState.entryCounter)
  ) {
    console.error("chord history length mismatch, should never happen", newState);
  }

  newState.primaryScaleId = findPrimaryScaleIdPastNTicks(newState, 20);
  newState.primaryScale = newState.primaryScaleId ? newState.chordsInScales[newState.primaryScaleId].scale : undefined;

  return newState;
}

/** metadata about each slice of time, for example "which scale is most relevant at this moment?"
 * Assumption: chord meta has already been filled in
 */
function buildTimestampMetaForOneTick(newState: AudioHistoryState, timeIndex: number): TimestampMeta {
  let mostLikelyScaleId: string | undefined = undefined;
  const scaleIds = Object.keys(newState.chordsInScales);
  if (scaleIds && scaleIds.length > 0) {
    // algo: "first scale which has any chords" is good enough.
    mostLikelyScaleId = scaleIds.find((sid) => (newState.chordsInScales[sid].chordHistory[timeIndex]?.length || 0) > 0);
  }
  return {
    mostLikelyScaleId,
  } as TimestampMeta;
}

function sliceNTicks(audioHistory: AudioHistoryState, scaleId: string, ticks: number | undefined): ChordInScaleEdit[][] {
  const chords = audioHistory.chordsInScales[scaleId].chordHistory;
  if (!ticks || audioHistory.entryCounter < ticks) {
    return chords;
  } else if (audioHistory.newestTimestampIndex === audioHistory.entryCounter - 1) {
    return chords.slice(0 - ticks);
  }
  const startInclusive = audioHistory.newestTimestampIndex - ticks;
  const endExclusive = audioHistory.newestTimestampIndex + 1;
  if (startInclusive < 0) {
    // rotate around to the end of the recording to get more data.
    // Because most likely the user just seeked from there, and we want to minimize how jumpy frontend pinning behavior is after seeking.
    return chords.slice(startInclusive).concat(chords.slice(0, endExclusive));
  }
  return chords.slice(startInclusive, endExclusive);
}

function findPrimaryScaleIdPastNTicks(audioHistory: AudioHistoryState, ticks: number | undefined): string | undefined {
  if (audioHistory.entryCounter < 5) {
    return undefined;
  }
  let maxChordSlots = 0;
  let maxChordSlotsScaleId: string | undefined = undefined;
  Object.keys(audioHistory.chordsInScales).forEach((sid) => {
    const ticksConsid = sliceNTicks(audioHistory, sid, ticks);
    const countSlotsWithChords = ticksConsid.map((c) => (c.length > 0 ? 1 : 0)).reduce((a: number, b: number) => a + b, 0);
    if (countSlotsWithChords > maxChordSlots) {
      maxChordSlots = countSlotsWithChords;
      maxChordSlotsScaleId = sid;
    }
  });
  return maxChordSlotsScaleId;
}

export function findPrimaryScalePastNTicks(audioHistory: AudioHistoryState, ticks: number | undefined): MusicScale | undefined {
  const scaleId = findPrimaryScaleIdPastNTicks(audioHistory, ticks);
  if (!scaleId) {
    return undefined;
  }
  return audioHistory.chordsInScales[scaleId as string].scale;
}

type ChordDeletionArgs = {
  scaleId: string;
  tickIndex: number;
  chordIndex: number;
};
export function mutateAudioHistoryDeleteChord(state: AudioHistoryState, { scaleId, tickIndex, chordIndex }: ChordDeletionArgs): void {
  const tickChords = state.chordsInScales[scaleId].chordHistory[tickIndex];
  tickChords.splice(chordIndex, 1);
  state.chordsInScales[scaleId].chordHistory[tickIndex] = [...tickChords];
  const prevHighest = state.chordsInScales[scaleId].highestNumOfChords;
  if (tickChords.length + 1 >= prevHighest) {
    const newMax = Math.max(...state.chordsInScales[scaleId].chordHistory.map((eachSetOfChords) => eachSetOfChords.length));
    state.chordsInScales[scaleId].highestNumOfChords = newMax;
  }
  if (tickChords.length === 0) {
    state.timestampMeta[tickIndex] = buildTimestampMetaForOneTick(state, tickIndex);
  }
}
export function mutateAudioHistoryDeleteScale(state: AudioHistoryState, scaleId: string) {
  delete state.chordsInScales[scaleId];
  for (let i = 0; i < state.entryCounter; i++) {
    if (state.timestampMeta[i].mostLikelyScaleId === scaleId) {
      state.timestampMeta[i] = buildTimestampMetaForOneTick(state, i);
    }
  }
}

/**
 * Organize state in terms of family because
 * (1) major/minor scales are redundant otherwise
 * (2) transition from penta to minor to harm/mel minor throws away information otherwise
 */
export function buildUniqueScaleFamilyId(scale: MusicScale): string {
  return scale.family.minor_tonic_id;
}

// Obvious pitfalls - we need new references to get react to render

function updateWeightHistory(oldHist: WeightHistory, wasmOutput: AudioProcessOutput): WeightHistory {
  let biggestSumWeightYet = oldHist.biggestSumWeightYet;
  (wasmOutput.last01s_loudest_notes || []).forEach((n) => {
    if (n.sum_weights > biggestSumWeightYet) {
      biggestSumWeightYet = n.sum_weights;
    }
  });
  let totalNoteWeights: { [noteId: string]: MusicNoteSimpleWeight } = {};
  wasmOutput.alltime_note_weights.forEach((w) => {
    totalNoteWeights[w.identity] = w;
  });

  return {
    biggestSumWeightYet,
    totalNoteWeights,
  };
}

function buildNextPlacement(howToAdd: HowToAdd, index: number): NextPlacement {
  return { howToAdd, index };
}

/**
 * Decide where to inject the latest value into the sorted history arrays, to maintain order.
 * Why necessary: Audio element lets the user seek to the future, or seek to the past to overwrite old history.
 * Alternative: let the render function(s) sort the entire array
 * Assumptions: (1) Arrays are already sorted from min to max timestamp. (2) timestamps are always intervals of AUDIO_BLOB_MILLIS
 */
function calculateNextPlacement(oldState: AudioHistoryState, latest: AudioProcessOutput): NextPlacement {
  const followsPreviousTick = oldState.newestTimestamp + AUDIO_BLOB_MILLIS === latest.time_stamp;
  const followsArrayEndTick = oldState.timestamps[oldState.timestamps.length - 1] + AUDIO_BLOB_MILLIS === latest.time_stamp;
  if (followsPreviousTick) {
    if (followsArrayEndTick) {
      return buildNextPlacement(HowToAdd.Append, oldState.timestamps.length);
    } else {
      const prevI = oldState.newestTimestampIndex;
      const doesNextTickExist = oldState.timestamps[prevI] + AUDIO_BLOB_MILLIS === oldState.timestamps[prevI + 1];
      if (doesNextTickExist) {
        return buildNextPlacement(HowToAdd.Replace, prevI + 1);
      } else {
        return buildNextPlacement(HowToAdd.Inject, prevI + 1);
      }
    }
  } else {
    const isNewTsBiggest = oldState.timestamps[oldState.timestamps.length - 1] < latest.time_stamp;
    if (isNewTsBiggest) {
      return buildNextPlacement(HowToAdd.Append, oldState.timestamps.length);
    } else {
      // find the biggest timestamp which is smaller than this one
      const nextBiggestTsIndex = oldState.timestamps.findIndex((oldT) => oldT > latest.time_stamp);
      const newTsAlreadyExists = oldState.timestamps[nextBiggestTsIndex - 1] === latest.time_stamp;
      if (newTsAlreadyExists) {
        return buildNextPlacement(HowToAdd.Replace, nextBiggestTsIndex - 1);
      } else {
        return buildNextPlacement(HowToAdd.Inject, nextBiggestTsIndex);
      }
    }
  }
}

/**
 * Generic solution to "put in the new thing to each array at index N, insert/replace or (simplest case) add to end."
 * Must create a new array reference.
 */
function buildNextHistoryArrayPerPlacement<T>(history: Array<T>, newItem: T, placement: NextPlacement): Array<T> {
  switch (placement.howToAdd) {
    case HowToAdd.Append:
      return [...history, newItem];
    case HowToAdd.Inject:
      const arrBefore = placement.index === 0 ? [] : history.slice(0, placement.index);
      const arrAfter = history.slice(placement.index); // include element previously at this index
      return [...arrBefore, newItem, ...arrAfter];
    case HowToAdd.Replace:
      const before = placement.index === 0 ? [] : history.slice(0, placement.index);
      const after = history.slice(placement.index + 1); // exclude element previously at this index
      return [...before, newItem, ...after];
  }
}
